import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Newspaper, ShieldAlert, Eye, Clock, ChevronDown } from 'lucide-react';
import AuthModal from './AuthModal';

const NavPanel = () => {
  const [searchParams] = useSearchParams();
  const currentView = searchParams.get('view') || 'all';
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const navItems = [
    { 
      name: 'Break the Bubble', 
      icon: ShieldAlert, 
      path: '/?view=all',
      description: 'See how different sources report the same story with varying perspectives and sentiments'
    },
    { 
      name: 'Blindsyde', 
      icon: Eye, 
      path: '/?view=blindsyde',
      description: 'Underreported stories with only 1-2 sources - discover news others might be missing'
    },
    { 
      name: 'Timeline', 
      icon: Clock, 
      path: '/?view=timeline',
      description: 'Follow breaking stories as they develop - events ordered chronologically with factual reporting'
    },
  ];

  const currentNavItem = navItems.find(item => item.path.includes(currentView)) || navItems[0];

  const handleNavClick = (e, item) => {
    if (item.requiresAuth) {
      const isLoggedIn = localStorage.getItem('user');
      if (!isLoggedIn) {
        e.preventDefault();
        setShowAuthModal(true);
      }
    }
    setMobileDropdownOpen(false);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-14 sm:top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <div className="hidden sm:flex space-x-6 lg:space-x-8">
            {navItems.map((item) => {
              const isActive = currentView === item.path.split('=')[1];
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`flex items-center space-x-2 py-3 sm:py-4 border-b-2 transition-colors group relative text-sm ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium">{item.name}</span>
                  
                  {/* Tooltip - Desktop only */}
                  <div className="absolute top-full left-0 mt-2 w-72 bg-gray-900 text-white text-sm rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 hidden lg:block">
                    {item.description}
                    {item.requiresAuth && (
                      <span className="block mt-1 text-xs text-gray-300">Requires login</span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile Navigation Dropdown */}
          <div className="sm:hidden">
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-900"
            >
              <div className="flex items-center space-x-2">
                <currentNavItem.icon className="w-5 h-5 text-blue-600" />
                <span>{currentNavItem.name}</span>
              </div>
              <ChevronDown className={`w-5 h-5 transition-transform ${
                mobileDropdownOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {mobileDropdownOpen && (
              <div className="absolute left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
                {navItems.map((item) => {
                  const isActive = currentView === item.path.split('=')[1];
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={(e) => handleNavClick(e, item)}
                      className={`flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 ${
                        isActive ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      )}
    </>
  );
};

export default NavPanel;
